import { useMemo } from 'react';

import { WidgetContentEntry, WidgetContentLayout, WidgetContentLayoutSubType } from '@vestico/model';

import { FixedRowLayout, GridLayout, RowLayout, RowLayoutScroll } from './carousel';
import { BaseLayoutProps } from './model/baseLayoutProps';
import { FeedLayout, SnakeLayout } from './page';
import { PopupLayout } from './popup';
import { StoriesLayout } from './stories';

export interface ContentLayoutProps extends BaseLayoutProps {
  entry: WidgetContentEntry;
  loading: boolean;
  loadMore: () => void;
  hasMore: boolean;
  clientID: string;
  useMobileLayout: boolean;
  onAppear?: () => void;
}

export const ContentLayout = ({
  entry,
  responses,
  onResponseClick,
  showUserMatchInfoCTA,
  onBeginUserMatchInfo,
  loading,
  loadMore,
  hasMore,
  clientID,
  useMobileLayout,
  onAppear,
}: ContentLayoutProps) => {
  const { mobileLayout, category } = entry;

  const layout = useMemo(() => {
    const layoutForLayout = (layoutType: WidgetContentLayoutSubType, inputLayout: WidgetContentLayout) => {
      switch (layoutType) {
        case 'dynamic':
          return (
            <SnakeLayout
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              showUserMatchInfoCTA={showUserMatchInfoCTA}
              onBeginUserMatchInfo={onBeginUserMatchInfo}
              loading={loading}
              loadMore={loadMore}
              category={category}
              clientID={clientID}
              hasMore={hasMore}
            />
          );
        case 'grid':
          return (
            <GridLayout
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              clientID={clientID}
            />
          );
        case 'singleRow':
          return (
            <RowLayout
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              clientID={clientID}
            />
          );
        case 'singleRowFixed':
          return (
            <FixedRowLayout
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              clientID={clientID}
            />
          );
        case 'singleRowFreeScroll':
          return (
            <RowLayoutScroll
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              clientID={clientID}
            />
          );
        case 'feed':
          return (
            <FeedLayout
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              showUserMatchInfoCTA={showUserMatchInfoCTA}
              onBeginUserMatchInfo={onBeginUserMatchInfo}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              category={category}
              clientID={clientID}
            />
          );
        case 'stories':
          return (
            <StoriesLayout
              layout={inputLayout}
              responses={responses}
              onResponseClick={onResponseClick}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              clientID={clientID}
            />
          );
        case 'popup':
          return <PopupLayout layout={inputLayout} responses={responses} onResponseClick={onResponseClick} clientID={clientID} onAppear={onAppear} />;
        default:
          throw new Error(`Unhandled layout ${layoutType}`);
      }
    };

    if (mobileLayout && useMobileLayout) {
      return layoutForLayout(mobileLayout.layoutSubType, mobileLayout);
    }

    return layoutForLayout(entry.layoutSubType, entry);
  }, [
    mobileLayout,
    useMobileLayout,
    entry,
    responses,
    onResponseClick,
    showUserMatchInfoCTA,
    onBeginUserMatchInfo,
    loading,
    loadMore,
    category,
    clientID,
    hasMore,
    onAppear,
  ]);

  return layout;
};
